import { lazy } from 'react';

import type { ConfirmationModalProps } from 'components/modals';
import type {
  DownloadInvoicesModalProps,
  PasswordModalProps,
  SoftwareTwoFactorAuthenticationModalProps,
} from 'modules/accounts/modals';
import type { AddCreditCardModalProps, ManageCreditCardModalProps, TopUpModalProps } from 'modules/billing/modals';
import type {
  CountryRequestModalProps,
  CouponCodeModalProps,
  OrderCheckoutStatusModalProps,
} from 'modules/orders/modals';
import type {
  OrganizationAddMemberModalProps,
  OrganizationChangeMemberRoleModalProps,
  OrganizationRemoveMemberModalProps,
} from 'modules/organizations/modals';
import type {
  ProxyAdminChangePortModalProps,
  ProxyAdminChangelogModalProps,
  ProxyAdminDiagnosticModalProps,
  ProxyAdminEventsModalProps,
  ProxyAdminRefundModalProps,
  ProxyAdminReviveModalProps,
  ProxyAdminAddRouteModalProps,
  ProxyAutoExtendBandwidthModalProps,
  ProxyAutoExtendModalProps,
  ProxyBulkAutoExtendModalProps,
  ProxyBulkChangeAuthenticationMethodModalProps,
  ProxyBulkChangeProtocolModalProps,
  ProxyBulkExtendBandwidthModalProps,
  ProxyBulkExtendPeriodModalProps,
  ProxyBulkReactivateModalProps,
  ProxyChangeAuthenticationMethodModalProps,
  ProxyChangeProtocolModalProps,
  ProxyChangeThreadsModalProps,
  ProxyChangeUplinkSpeedModalProps,
  ProxyCopyCredentialsModalProps,
  ProxyDownloadCredentialsModalProps,
  ProxyExtendBandwidthModalProps,
  ProxyExtendPeriodModalProps,
  ProxyGeneratedCredentialsModalProps,
  ProxyGlobalIPWhitelistModalProps,
  ProxyIPv6CredentialsModalProps,
  ProxyReactivateModalProps,
  ProxyUpgradeThreadsModalProps,
  ProxyUpgradeUplinkSpeedModalProps,
  ReplaceProxiesProgressModalProps,
} from 'modules/proxies/modals';
import type { PayoutSetupModalProps } from 'modules/referrals/modals';
import type { WebhookModalProps } from 'modules/webhooks/modals';

import { createModalManager } from './context';
import type { Modal } from './types';

// #region Lazy modals
// Every modal used to be statically imported here, which meant all of them (including
// @stripe/stripe-js via TopUpModal/AddCreditCardModal) shipped in the main bundle and were
// parsed/executed on every page load, regardless of whether a modal was ever opened.
// `createLazyImport` isn't used here since its generics assume one component per module and
// can't cleanly infer per-export prop types out of these barrels with many differently-typed
// named exports - `lazy()` + a plain property access lets each one infer its own props.
const ConfirmationModal = lazy(() => import('components/modals').then((m) => ({ default: m.ConfirmationModal })));

const DownloadInvoicesModal = lazy(() =>
  import('modules/accounts/modals').then((m) => ({ default: m.DownloadInvoicesModal })),
);
const InvoicePDFDeliveryModal = lazy(() =>
  import('modules/accounts/modals').then((m) => ({ default: m.InvoicePDFDeliveryModal })),
);
const PasswordModal = lazy(() => import('modules/accounts/modals').then((m) => ({ default: m.PasswordModal })));
const SoftwareTwoFactorAuthenticationModal = lazy(() =>
  import('modules/accounts/modals').then((m) => ({ default: m.SoftwareTwoFactorAuthenticationModal })),
);
const VerifyEmailModal = lazy(() => import('modules/accounts/modals').then((m) => ({ default: m.VerifyEmailModal })));

const AddCreditCardModal = lazy(() =>
  import('modules/billing/modals').then((m) => ({ default: m.AddCreditCardModal })),
);
const ManageCreditCardModal = lazy(() =>
  import('modules/billing/modals').then((m) => ({ default: m.ManageCreditCardModal })),
);
const TopUpModal = lazy(() => import('modules/billing/modals').then((m) => ({ default: m.TopUpModal })));

const CountryRequestModal = lazy(() =>
  import('modules/orders/modals').then((m) => ({ default: m.CountryRequestModal })),
);
const CouponCodeModal = lazy(() => import('modules/orders/modals').then((m) => ({ default: m.CouponCodeModal })));
const OrderCheckoutStatusModal = lazy(() =>
  import('modules/orders/modals').then((m) => ({ default: m.OrderCheckoutStatusModal })),
);

const OrganizationAddMemberModal = lazy(() =>
  import('modules/organizations/modals').then((m) => ({ default: m.OrganizationAddMemberModal })),
);
const OrganizationChangeMemberRoleModal = lazy(() =>
  import('modules/organizations/modals').then((m) => ({ default: m.OrganizationChangeMemberRoleModal })),
);
const OrganizationRemoveMemberModal = lazy(() =>
  import('modules/organizations/modals').then((m) => ({ default: m.OrganizationRemoveMemberModal })),
);

const ProxyAdminAddRouteModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminAddRouteModal })),
);
const ProxyAdminChangePortModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminChangePortModal })),
);
const ProxyAdminChangelogModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminChangelogModal })),
);
const ProxyAdminDiagnosticModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminDiagnosticModal })),
);
const ProxyAdminEventsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminEventsModal })),
);
const ProxyAdminRefundModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminRefundModal })),
);
const ProxyAdminReviveModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAdminReviveModal })),
);
const ProxyAutoExtendBandwidthModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAutoExtendBandwidthModal })),
);
const ProxyAutoExtendModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyAutoExtendModal })),
);
const ProxyBulkAutoExtendModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyBulkAutoExtendModal })),
);
const ProxyBulkChangeAuthenticationMethodModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyBulkChangeAuthenticationMethodModal })),
);
const ProxyBulkChangeProtocolModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyBulkChangeProtocolModal })),
);
const ProxyBulkExtendBandwidthModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyBulkExtendBandwidthModal })),
);
const ProxyBulkExtendPeriodModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyBulkExtendPeriodModal })),
);
const ProxyBulkReactivateModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyBulkReactivateModal })),
);
const ProxyChangeAuthenticationMethodModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyChangeAuthenticationMethodModal })),
);
const ProxyChangeProtocolModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyChangeProtocolModal })),
);
const ProxyChangeThreadsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyChangeThreadsModal })),
);
const ProxyChangeUplinkSpeedModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyChangeUplinkSpeedModal })),
);
const ProxyCopyCredentialsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyCopyCredentialsModal })),
);
const ProxyDownloadCredentialsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyDownloadCredentialsModal })),
);
const ProxyExtendBandwidthModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyExtendBandwidthModal })),
);
const ProxyExtendPeriodModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyExtendPeriodModal })),
);
const ProxyGeneratedCredentialsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyGeneratedCredentialsModal })),
);
const ProxyGlobalIPWhitelistModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyGlobalIPWhitelistModal })),
);
const ProxyIPv6CredentialsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyIPv6CredentialsModal })),
);
const ProxyReactivateModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyReactivateModal })),
);
const ProxyUpgradeThreadsModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyUpgradeThreadsModal })),
);
const ProxyUpgradeUplinkSpeedModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ProxyUpgradeUplinkSpeedModal })),
);
const ReplaceProxiesProgressModal = lazy(() =>
  import('modules/proxies/modals').then((m) => ({ default: m.ReplaceProxiesProgressModal })),
);

const PayoutModal = lazy(() => import('modules/referrals/modals').then((m) => ({ default: m.PayoutModal })));
const PayoutNotPossibleModal = lazy(() =>
  import('modules/referrals/modals').then((m) => ({ default: m.PayoutNotPossibleModal })),
);
const PayoutSetupModal = lazy(() => import('modules/referrals/modals').then((m) => ({ default: m.PayoutSetupModal })));

const WebhookModal = lazy(() => import('modules/webhooks/modals').then((m) => ({ default: m.WebhookModal })));
// #endregion Lazy modals

type ModalMapType =
  | Modal<'credit.card.add', AddCreditCardModalProps>
  | Modal<'credit.card.remove', ConfirmationModalProps>
  | Modal<'orders.coupon', CouponCodeModalProps>
  | Modal<'orders.cancel', ConfirmationModalProps>
  | Modal<'proxies.admin.https.toggle', ConfirmationModalProps>
  | Modal<'account.totp', SoftwareTwoFactorAuthenticationModalProps>
  | Modal<'account.password', PasswordModalProps>
  | Modal<'account.id.verification', ConfirmationModalProps>
  | Modal<'account.invoice.manage'>
  | Modal<'account.email.verification'>
  | Modal<'credit.card.manage', ManageCreditCardModalProps>
  | Modal<'account.billing.topUp', TopUpModalProps>
  | Modal<'referrals.payout'>
  | Modal<'referrals.payout-not-possible'>
  | Modal<'referrals.payout-setup', PayoutSetupModalProps>
  | Modal<'account.billing.topUp.confirmation', ConfirmationModalProps>
  | Modal<'proxies.cancel.confirmation', ConfirmationModalProps>
  | Modal<'api-keys.delete', ConfirmationModalProps>
  | Modal<'order.status', OrderCheckoutStatusModalProps>
  | Modal<'order.invoice', ConfirmationModalProps>
  | Modal<'order.no-deals', ConfirmationModalProps>
  | Modal<'account.invoices.download', DownloadInvoicesModalProps>
  | Modal<'order.request.country', CountryRequestModalProps>
  | Modal<'proxies.admin.changelog', ProxyAdminChangelogModalProps>
  | Modal<'proxies.admin.diagnostic', ProxyAdminDiagnosticModalProps>
  | Modal<'proxies.admin.events', ProxyAdminEventsModalProps>
  | Modal<'proxies.admin.port.change', ProxyAdminChangePortModalProps>
  | Modal<'proxies.admin.refund', ProxyAdminRefundModalProps>
  | Modal<'proxies.admin.refund.confirmation', ConfirmationModalProps>
  | Modal<'proxies.admin.revive', ProxyAdminReviveModalProps>
  | Modal<'proxies.admin.route.add', ProxyAdminAddRouteModalProps>
  | Modal<'proxies.admin.route.delete', ConfirmationModalProps>
  | Modal<'proxies.authentication-method.change', ProxyChangeAuthenticationMethodModalProps>
  | Modal<'proxies.authentication-method.change.bulk', ProxyBulkChangeAuthenticationMethodModalProps>
  | Modal<'proxies.bandwidth.auto-extend', ProxyAutoExtendBandwidthModalProps>
  | Modal<'proxies.bandwidth.extend', ProxyExtendBandwidthModalProps>
  | Modal<'proxies.bandwidth.extend.bulk', ProxyBulkExtendBandwidthModalProps>
  | Modal<'proxies.credentials.copy', ProxyCopyCredentialsModalProps>
  | Modal<'proxies.credentials.download', ProxyDownloadCredentialsModalProps>
  | Modal<'proxies.credentials.generated', ProxyGeneratedCredentialsModalProps>
  | Modal<'proxies.credentials.ipv6', ProxyIPv6CredentialsModalProps>
  | Modal<'proxies.period.auto-extend', ProxyAutoExtendModalProps>
  | Modal<'proxies.period.auto-extend.bulk', ProxyBulkAutoExtendModalProps>
  | Modal<'proxies.period.extend', ProxyExtendPeriodModalProps>
  | Modal<'proxies.period.extend.bulk', ProxyBulkExtendPeriodModalProps>
  | Modal<'proxies.protocol.change', ProxyChangeProtocolModalProps>
  | Modal<'proxies.protocol.change.bulk', ProxyBulkChangeProtocolModalProps>
  | Modal<'proxies.reactivate', ProxyReactivateModalProps>
  | Modal<'proxies.reactivate.bulk', ProxyBulkReactivateModalProps>
  | Modal<'proxies.replace.progress', ReplaceProxiesProgressModalProps>
  | Modal<'proxies.threads.change', ProxyChangeThreadsModalProps>
  | Modal<'proxies.threads.upgrade', ProxyUpgradeThreadsModalProps>
  | Modal<'proxies.uplink-speed.change', ProxyChangeUplinkSpeedModalProps>
  | Modal<'proxies.uplink-speed.upgrade', ProxyUpgradeUplinkSpeedModalProps>
  | Modal<'proxies.whitelist.global', ProxyGlobalIPWhitelistModalProps>
  | Modal<'organizations.member.add', OrganizationAddMemberModalProps>
  | Modal<'organizations.member.role.change', OrganizationChangeMemberRoleModalProps>
  | Modal<'organizations.member.remove', OrganizationRemoveMemberModalProps>
  | Modal<'webhook.create', WebhookModalProps>
  | Modal<'webhook.delete', ConfirmationModalProps>
  | Modal<'webhook.edit', WebhookModalProps>;

// Critical, revenue-impacting flows - session recording is turned on for the duration any of
// these modals is open (see createModalManager / utils/sessionRecording), independent of errors.
const CRITICAL_RECORDING_MODAL_IDS = new Set<ModalMapType['id']>([
  'account.billing.topUp',
  'proxies.period.extend',
  'proxies.period.extend.bulk',
  'proxies.period.auto-extend',
  'proxies.period.auto-extend.bulk',
  'proxies.bandwidth.extend',
  'proxies.bandwidth.extend.bulk',
  'proxies.bandwidth.auto-extend',
]);

const { Provider, useShowModal, useHideModal } = createModalManager<ModalMapType>(({ id, params }) => {
  switch (id) {
    case 'credit.card.add':
      return <AddCreditCardModal {...params} />;
    case 'account.billing.topUp':
      return <TopUpModal {...params} />;
    case 'account.invoice.manage':
      return <InvoicePDFDeliveryModal {...params} />;
    case 'credit.card.manage':
      return <ManageCreditCardModal {...params} />;
    case 'orders.coupon':
      return <CouponCodeModal {...params} />;
    case 'orders.cancel':
    case 'order.no-deals':
    case 'order.invoice':
    case 'credit.card.remove':
    case 'account.id.verification':
    case 'account.billing.topUp.confirmation':
    case 'api-keys.delete':
    case 'proxies.cancel.confirmation':
    case 'proxies.admin.https.toggle':
    case 'proxies.admin.route.delete':
    case 'proxies.admin.refund.confirmation':
    case 'webhook.delete':
      return <ConfirmationModal {...params} />;
    case 'account.totp':
      return <SoftwareTwoFactorAuthenticationModal {...params} />;
    case 'account.password':
      return <PasswordModal {...params} />;
    case 'referrals.payout':
      return <PayoutModal />;
    case 'referrals.payout-not-possible':
      return <PayoutNotPossibleModal />;
    case 'referrals.payout-setup':
      return <PayoutSetupModal {...params} />;
    case 'order.status':
      return <OrderCheckoutStatusModal {...params} />;
    case 'account.email.verification':
      return <VerifyEmailModal />;
    case 'account.invoices.download':
      return <DownloadInvoicesModal {...params} />;
    case 'order.request.country':
      return <CountryRequestModal {...params} />;
    case 'proxies.admin.changelog':
      return <ProxyAdminChangelogModal {...params} />;
    case 'proxies.admin.diagnostic':
      return <ProxyAdminDiagnosticModal {...params} />;
    case 'proxies.admin.events':
      return <ProxyAdminEventsModal {...params} />;
    case 'proxies.admin.port.change':
      return <ProxyAdminChangePortModal {...params} />;
    case 'proxies.admin.refund':
      return <ProxyAdminRefundModal {...params} />;
    case 'proxies.admin.revive':
      return <ProxyAdminReviveModal {...params} />;
    case 'proxies.admin.route.add':
      return <ProxyAdminAddRouteModal {...params} />;
    case 'proxies.authentication-method.change':
      return <ProxyChangeAuthenticationMethodModal {...params} />;
    case 'proxies.authentication-method.change.bulk':
      return <ProxyBulkChangeAuthenticationMethodModal {...params} />;
    case 'proxies.bandwidth.auto-extend':
      return <ProxyAutoExtendBandwidthModal {...params} />;
    case 'proxies.bandwidth.extend':
      return <ProxyExtendBandwidthModal {...params} />;
    case 'proxies.bandwidth.extend.bulk':
      return <ProxyBulkExtendBandwidthModal {...params} />;
    case 'proxies.credentials.copy':
      return <ProxyCopyCredentialsModal {...params} />;
    case 'proxies.credentials.download':
      return <ProxyDownloadCredentialsModal {...params} />;
    case 'proxies.credentials.generated':
      return <ProxyGeneratedCredentialsModal {...params} />;
    case 'proxies.credentials.ipv6':
      return <ProxyIPv6CredentialsModal {...params} />;
    case 'proxies.period.auto-extend':
      return <ProxyAutoExtendModal {...params} />;
    case 'proxies.period.auto-extend.bulk':
      return <ProxyBulkAutoExtendModal {...params} />;
    case 'proxies.period.extend':
      return <ProxyExtendPeriodModal {...params} />;
    case 'proxies.period.extend.bulk':
      return <ProxyBulkExtendPeriodModal {...params} />;
    case 'proxies.protocol.change':
      return <ProxyChangeProtocolModal {...params} />;
    case 'proxies.protocol.change.bulk':
      return <ProxyBulkChangeProtocolModal {...params} />;
    case 'proxies.reactivate':
      return <ProxyReactivateModal {...params} />;
    case 'proxies.reactivate.bulk':
      return <ProxyBulkReactivateModal {...params} />;
    case 'proxies.replace.progress':
      return <ReplaceProxiesProgressModal {...params} />;
    case 'proxies.threads.change':
      return <ProxyChangeThreadsModal {...params} />;
    case 'proxies.threads.upgrade':
      return <ProxyUpgradeThreadsModal {...params} />;
    case 'proxies.uplink-speed.change':
      return <ProxyChangeUplinkSpeedModal {...params} />;
    case 'proxies.uplink-speed.upgrade':
      return <ProxyUpgradeUplinkSpeedModal {...params} />;
    case 'proxies.whitelist.global':
      return <ProxyGlobalIPWhitelistModal {...params} />;
    case 'organizations.member.add':
      return <OrganizationAddMemberModal />;
    case 'organizations.member.role.change':
      return <OrganizationChangeMemberRoleModal {...params} />;
    case 'organizations.member.remove':
      return <OrganizationRemoveMemberModal {...params} />;
    case 'webhook.create':
    case 'webhook.edit':
      return <WebhookModal {...params} />;
    default:
      return null;
  }
}, CRITICAL_RECORDING_MODAL_IDS);

export { useShowModal, useHideModal };

export default { Provider };
